import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import './styles/accessibility.css';
import ProtectedRoute from './components/ProtectedRoute';
import { ToastProvider } from './components/Toast';

// Import components
import Header from './components/Header';
import KeyboardShortcuts from './components/KeyboardShortcuts';
import KeyboardShortcutsHelp from './components/KeyboardShortcutsHelp';

// Import pages
import LandingPage from './pages/LandingPage';
import ClientLogin from './pages/ClientLogin';
import ClientRegistration from './pages/ClientRegistration'; // Import your real registration component
import ProviderLogin from './pages/ProviderLogin';
import ProviderSignup from './pages/ProviderSignup';
import SetPassword from './pages/SetPassword';
import ClientDashboard from './pages/ClientDashboard';
import ProviderDashboard from './pages/ProviderDashboard';
import UpdateProfile from './pages/UpdateProfile';
import ProviderInfo from './pages/ProviderInfo';
import BookAppointment from './pages/BookAppointment';
import CredentialingPage from './pages/CredentialingPage';
import DocumentManager from './components/client/DocumentManager';
import VideoSessionPage from './pages/VideoSessionPage';
import InstallPrompt from './components/InstallPrompt';

// Dev/Test pages — only loaded in development
const SessionTestPage = React.lazy(() => import('./pages/SessionTestPage'));
const FirebaseTest = React.lazy(() => import('./pages/FirebaseTest'));
const MigrationPage = React.lazy(() => import('./pages/MigrationPage'));
const HopeTest = React.lazy(() => import('./pages/HopeTest'));
const HopeUIDemo = React.lazy(() => import('./pages/HopeUIDemo'));
const FloatingHopeDemo = React.lazy(() => import('./pages/FloatingHopeDemo'));
const Convai3DDemo = React.lazy(() => import('./pages/Convai3DDemo'));
const HopeFullScreen = React.lazy(() => import('./pages/HopeFullScreen'));

const App: React.FC = () => {
  return (
    <ToastProvider>
    <Router>
      <div className="app">
        {/* Global keyboard shortcuts for accessibility */}
        <KeyboardShortcuts />

        {/* Keyboard shortcuts help modal */}
        <KeyboardShortcutsHelp />

        {/* Skip to main content link for keyboard navigation */}
        <a href="#main-content" className="skip-link">
          Skip to main content
        </a>

        <main id="main-content">
          <Routes>
          {/* Landing Page Route */}
          <Route path="/" element={<LandingPage />} />
          
          {/* Authentication Routes */}
          <Route path="/client-login" element={<ClientLogin />} />
          {/* Route stays mounted because staff-issued patient invites land
              here (FirebaseInviteService builds /client-register?invite=<id>).
              ClientRegistration itself refuses when there is no invite token,
              so self-registration is closed without breaking invites. */}
          <Route path="/client-register" element={<ClientRegistration />} />
          <Route path="/provider-login" element={<ProviderLogin />} />
          <Route path="/provider-signup" element={<ProviderSignup />} />
          {/* Emailed set-password links land here instead of Firebase's unbranded
              firebaseapp.com page. Public on purpose: the one-time code is the credential. */}
          <Route path="/set-password" element={<SetPassword />} />
          
          {/* Dashboard Routes - Protected */}
          <Route
            path="/client-dashboard"
            element={
              <ProtectedRoute requiredRole="client">
                <ClientDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/provider-dashboard"
            element={
              <ProtectedRoute allowedRoles={['provider', 'system_admin', 'clinical_director', 'administrative_staff', 'hr_admin']}>
                <ProviderDashboard />
              </ProtectedRoute>
            }
          />
          <Route path="/update-profile" element={<UpdateProfile />} />
          <Route path="/provider-info" element={<ProviderInfo />} />
          <Route path="/book-appointment" element={<BookAppointment />} />
          <Route path="/credentialing" element={<CredentialingPage />} />

          {/* Video Session — opens in popup window */}
          <Route
            path="/video-session"
            element={
              <ProtectedRoute allowedRoles={['client', 'provider', 'system_admin', 'clinical_director']}>
                <VideoSessionPage />
              </ProtectedRoute>
            }
          />

          {/* Document Routes */}
          <Route path="/documents" element={<DocumentManager clientId="temp_client_id" />} />

          {/* Dev/Test Routes — hidden in production */}
          {process.env.NODE_ENV === 'development' && (
            <>
              <Route path="/session-test" element={<React.Suspense fallback={<div>Loading...</div>}><SessionTestPage /></React.Suspense>} />
              <Route path="/firebase-test" element={<React.Suspense fallback={<div>Loading...</div>}><FirebaseTest /></React.Suspense>} />
              <Route path="/migration" element={<React.Suspense fallback={<div>Loading...</div>}><MigrationPage /></React.Suspense>} />
              <Route path="/hope-test" element={<React.Suspense fallback={<div>Loading...</div>}><HopeTest /></React.Suspense>} />
              <Route path="/hope-ui-demo" element={<React.Suspense fallback={<div>Loading...</div>}><HopeUIDemo /></React.Suspense>} />
              <Route path="/floating-hope-demo" element={<React.Suspense fallback={<div>Loading...</div>}><FloatingHopeDemo /></React.Suspense>} />
              <Route path="/convai-3d-demo" element={<React.Suspense fallback={<div>Loading...</div>}><Convai3DDemo /></React.Suspense>} />
              <Route path="/hope-fullscreen" element={<React.Suspense fallback={<div>Loading...</div>}><HopeFullScreen /></React.Suspense>} />
            </>
          )}


          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        </main>

        {/* PWA Install Prompt */}
        <InstallPrompt />
      </div>
    </Router>
    </ToastProvider>
  );
};

export default App;